import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import {
  createQueryParams,
  createShouldClause,
  type ElasticsearchQueryFilter,
  QUERY_DEFAULTS,
} from '@/shared/api/openmetadata';
import type { OpenMetadataResponse } from '@/shared/api/openmetadata';
import { escapeSearchQuery } from '@/shared/lib/sanitize';
import type { SortOption } from '@/shared/types/common';

import type { FilterState } from '@/entities/discovery/domain/types';

const buildTaggedAssetsQueryFilter = (termFqn: string, filters: FilterState): ElasticsearchQueryFilter => {
  const mustClauses: Array<Record<string, unknown>> = [{ term: { 'tags.tagFQN': termFqn } }];

  if (filters.assetType && filters.assetType.length > 0) {
    mustClauses.push(createShouldClause('entityType', filters.assetType));
  }

  if (filters.dataSource && filters.dataSource.length > 0) {
    const serviceTypes = filters.dataSource.map((ds) => ds.split('.')[0]);
    mustClauses.push(createShouldClause('serviceType', serviceTypes));
  }

  if (filters.owner && filters.owner.length > 0) {
    mustClauses.push(createShouldClause('owners.displayName.keyword', filters.owner));
  }

  if (filters.tier && filters.tier.length > 0) {
    mustClauses.push(createShouldClause('tier.tagFQN', filters.tier));
  }

  if (filters.certification && filters.certification.length > 0) {
    mustClauses.push(createShouldClause('certification.tagLabel.tagFQN', filters.certification));
  }

  return {
    query: {
      bool: {
        must: mustClauses,
      },
    },
  };
};

export const searchTaggedAssets = async (
  termFqn: string,
  filters: FilterState,
  query: string = '',
  from: number = 0,
  size: number = QUERY_DEFAULTS.SIZE.SEARCH_PAGE,
  sortBy: SortOption['field'],
  sortOrder: SortOption['direction']
): Promise<OpenMetadataResponse> => {
  // OpenSearch 특수 문자 이스케이핑 (파싱 에러 방지)
  const escapedQuery = query ? escapeSearchQuery(query) : '';

  const params = createQueryParams({
    q: escapedQuery ? `displayName:*${escapedQuery}* OR description:*${escapedQuery}*` : QUERY_DEFAULTS.Q,
    index: 'all',
    from,
    size,
    deleted: false,
    sort_field: sortBy,
    sort_order: sortOrder,
    exclude_source_fields: 'columns',
  });

  const queryFilter = buildTaggedAssetsQueryFilter(termFqn, filters);
  params.query_filter = JSON.stringify(queryFilter);

  const response = await openMetadataApiClient.get<OpenMetadataResponse>(`/search/query`, {
    params,
    paramsSerializer: {
      encode: (value) => encodeURIComponent(value),
    },
  });

  return response.data;
};
